import { hasCookie, getCookie } from './cookieHelpers';

// constants
export const INSIGHT_CURRENT_LOCALE_COOKIE_NAME = 'insight_current_locale';
export const REGIONS = {
  EMEA: 'EMEA',
  APAC: 'APAC',
  NA: 'NA',
};
const emeaCodes = [
  'at',
  'be',
  'ch',
  'de',
  'dk',
  'es',
  'fi',
  'fr',
  'gb',
  'ie',
  'it',
  'nl',
  'no',
  'se',
  'ue',
];
const apacCodes = ['au', 'cn', 'hk', 'jp', 'nz', 'sg'];

/**
 * get current locale from cookie for Currency and Date formatting
 * @property {String} currentLocale //current locale cookie name
 * @property {String} domainLocale //domain locale cookie name
 **/
export function getCurrentLocale(currentLocaleCookie, domainCookie) {
  //get new current locale cookie.  Fallback to domain cookie if it does not exist for backwards compatibility
  const locale = hasCookie(currentLocaleCookie)
    ? getCookie(currentLocaleCookie)
    : hasCookie(domainCookie)
    ? getCookie(domainCookie)
    : 'en_US'
  return locale
}

/**
 * get region from domain cookie
 * @property {String} domainLocale //domain locale cookie name
 **/
export function getRegion(domainCookie) {
  //get country code from domain locale cookie.
  const country = getCountryCode(domainCookie);

  if (emeaCodes.includes(country.toLowerCase())) {
    return REGIONS.EMEA;
  } else if (apacCodes.includes(country.toLowerCase())) {
    return REGIONS.APAC;
  } else {
    return REGIONS.NA;
  }
}

/**
 * get country code from domain cookie
 * @property {String} domainLocale //domain locale cookie name
 **/
export function getCountryCode(domainCookie) {
  return hasCookie(domainCookie) &&
    getCookie(domainCookie).split('_').length == 2
    ? getCookie(domainCookie).split('_')[1]
    : 'US'
}

/**
 * get display contact number by locale
 * @property {String} locale //current locale value, IE: en_US
 **/
export function getContactNumber(locale) {
  const localeToPhoneNumberMap = {
    AT: '07 20700 285',
    AU: '1800 189 888',
    BE: '+32 (0)2 263 60 20',
    CA: '1.800.INSIGHT',
    CH: '0848 022 022',
    CN: '+86 21 6037 2820',
    DE: '06134 288 288',
    ES: '(00 34) 91 384 0790',
    FR: '+33 (0)1 30 67 25 00',
    GB: '0344 846 3333',
    HK: '852 2972 8200',
    IE: '00353 (0)155 30 110', //non-transactional, used on header and AEM pages
    IT: '+39 02 21080210',
    NL: '055 538 2320',
    NZ: '0800 933 111',
    SE: '+46 (0)8 522 100 10', //handles redirects from DE, FI, NO
    SG: '65 6438 2995',
    US: '1.800.INSIGHT',
  }

  const countryCode = locale.split('_')[1].toUpperCase()

  return localeToPhoneNumberMap[countryCode] || localeToPhoneNumberMap.US
}

/**
 * get default logged-out user's salesOrg by locale
 * @property {String} locale //current locale value, IE: en_US
 * @property {Boolean} isIPS //is IPS user
 **/
export function getDefaultLoggedOutSalesOrg(locale, isIPS = false) {
  const localeToSalesOrgMap = {
    // NA Regions
    CA: '4100',
    US: isIPS ? '2500' : '2400',
    // EMEA Regions
    GB: '7000',
    IE: '7100',
    SE: '7200',
    DE: '7600',
    NL: '7700',
    BE: '7800',
    AT: '8000',
    CH: '8100',
    FR: '8200',
    IT: '8300',
    ES: '8400',
    AU: '6500',
    CN: '6300',
    HK: '6200',
    NZ: '6600',
    SG: '6100',
    //note: future locale can be added once they support default logged-out experience
  }

  const countryCode = locale.split('_')[1].toUpperCase()

  return localeToSalesOrgMap[countryCode] || null
}

/**
 * Check EMEA region based on locale
 **/
export const isEmeaRegion = () => {
  const region = getRegion(INSIGHT_CURRENT_LOCALE_COOKIE_NAME);
  return region === REGIONS.EMEA;
}
 
/**
 * Check APAC region based on locale
 **/
export const isApacRegion = () => {
  const region = getRegion(INSIGHT_CURRENT_LOCALE_COOKIE_NAME);
  return region === REGIONS.APAC;
}

// Function to return the login URL
export const getLoginURL = () => {
  return '/insightweb/login';
}

// Map of locales to determine if simplified create account URL should be used
const useSimplifiedCreateAccountLocaleMap = {
  CA: true, // Canada
  US: true, // United States
  AU: false, // Australia
  AT: true, // Austria
  BE: true, // Belgium
  CH: true, // Switzerland
  DE: true, // Germany
  ES: true, // Spain
  FR: true, // France
  GB: true, // United Kingdom
  IT: true, // Italy
};

// Function to get the create account URL based on the locale
export const getCreateAccountURL = () => {
  const locale = getCurrentLocale(INSIGHT_CURRENT_LOCALE_COOKIE_NAME, "insight_locale") || 'en_US'; // Default locale if null or undefined
  
  // Extract the country code from the locale
  const countryCode = locale?.split('_')[1]?.toUpperCase();

  // Determine if simplified create account URL should be used based on the country code
  return useSimplifiedCreateAccountLocaleMap[countryCode] 
    ? '/insightweb/endUser/createAccount' // Use simplified URL if true
    : `/${locale}/help/create-account.html`; // Use full URL based on locale if false
}